/**
 * Le déroulé de l'entrée d'une activité : ce qui se lit, et quand. Pur.
 *
 * Celui de l'ancien `TimerActivityStep`, au temps près :
 *  - 2 s d'annonce : « Ton activité va commencer... » pour une activité neuve, « Ton activité va
 *    reprendre... » pour une activité déjà commencée (le serveur a un chrono pour elle) ;
 *  - une activité déjà commencée reprend là, sans décompte ;
 *  - une activité neuve enchaîne le décompte de l'ancienne animation (`Countdown.json`, 5 s) : 3, 2
 *    et 1 une seconde chacun, puis le départ, deux secondes. L'animation finissait sur « GO », en
 *    dessin ; le texte du départ dit « C'est parti ! ».
 * Le chrono ne part qu'à la fin, ou quand l'élève passe l'animation.
 */

export type StartCountdownKind = "start" | "resume"

/** L'annonce, avant le décompte (ou la reprise). */
export const ANNOUNCE_MS = 2000
/** Un chiffre du décompte. */
export const COUNT_STEP_MS = 1000
/** Le départ, après le 1. */
export const GO_MS = 2000

export const START_COUNTDOWN_TEXTS = {
    start: "Ton activité va commencer...",
    resume: "Ton activité va reprendre...",
    go: "C'est parti !",
    skip: "Passer l'animation",
}

export type StartCountdownFrame =
    /** L'annonce, puis chaque chiffre, puis le départ : une étape chacun (sa clé). */
    | { step: "announce" | "3" | "2" | "1" | "go"; label: string; done: false }
    | { step: "done"; label: null; done: true }

/** Le temps total de l'entrée. */
export function startCountdownDuration(kind: StartCountdownKind): number {
    return kind === "resume" ? ANNOUNCE_MS : ANNOUNCE_MS + 3 * COUNT_STEP_MS + GO_MS
}

/** Ce qui se lit `elapsed` ms après le début de l'entrée. */
export function startCountdownFrame(kind: StartCountdownKind, elapsed: number): StartCountdownFrame {
    if (elapsed >= startCountdownDuration(kind)) return { step: "done", label: null, done: true }
    if (elapsed < ANNOUNCE_MS) return { step: "announce", label: START_COUNTDOWN_TEXTS[kind], done: false }
    const counted = Math.floor((elapsed - ANNOUNCE_MS) / COUNT_STEP_MS)
    if (counted < 3) {
        const digit = String(3 - counted) as "3" | "2" | "1"
        return { step: digit, label: digit, done: false }
    }
    return { step: "go", label: START_COUNTDOWN_TEXTS.go, done: false }
}
